import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Phone, Mail, MapPin, Sheet, User, Sparkles } from 'lucide-react';

export default function Customers({ currentUser }) {
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSheetsDialog, setShowSheetsDialog] = useState(false);
  const [sheetsConfig, setSheetsConfig] = useState({
    spreadsheet_id: '',
    sheet_name: 'Customers',
    start_row: 2,
    api_key: ''
  });
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleGoogleSheetsImport = async () => {
    if (!sheetsConfig.spreadsheet_id) {
      alert('Please enter Spreadsheet ID or URL');
      return;
    }

    setImporting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/import-sheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sheetsConfig)
      });

      if (response.ok) {
        const data = await response.json();
        const errorMsg = data.errors && data.errors.length > 0 
          ? `\n\nErrors:\n${data.errors.join('\n')}`
          : '';
        alert(`Import successful! ✅\n\nImported: ${data.imported}\nSkipped (duplicates): ${data.skipped}\nTotal rows: ${data.total_rows}${errorMsg}`);
        setShowSheetsDialog(false);
        fetchCustomers();
      } else {
        const error = await response.json();
        alert(`Import failed ❌\n\n${error.detail || 'Unknown error'}\n\nMake sure:\n1. Sheet is public ("Anyone with link")\n2. Spreadsheet ID is correct\n3. Sheet name matches`);
      }
    } catch (error) {
      console.error('Error importing from sheets:', error);
      alert('Error importing from Google Sheets');
    } finally {
      setImporting(false);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.phone.includes(searchQuery)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Customers</h1>
          <p className="text-muted-foreground mt-1">
            Manage customer database and history
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showSheetsDialog} onOpenChange={setShowSheetsDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={!!currentUser?.branch}>
                <Sheet className="mr-2 h-4 w-4" />
                Import from Google Sheets
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Import Customers from Google Sheets</DialogTitle>
                <DialogDescription>
                  Import customer data in bulk from your spreadsheet
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-md text-sm">
                  <p className="font-semibold mb-2">🔑 Get FREE Google Sheets API Key:</p>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li><a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener" className="text-blue-600 underline">Click here</a> → Create project</li>
                    <li>Enable "Google Sheets API"</li>
                    <li>Create API Key → Copy it → Paste below ⬇️</li>
                  </ol>
                </div>
                
                <div>
                  <Label htmlFor="api_key">Google Sheets API Key *</Label>
                  <Input
                    id="api_key"
                    type="password"
                    value={sheetsConfig.api_key}
                    onChange={(e) => setSheetsConfig({...sheetsConfig, api_key: e.target.value})}
                    placeholder="AIzaSy... (get free from link above)"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    One-time: Get free key from Google Cloud (link above)
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="spreadsheet_id">Spreadsheet ID or URL *</Label>
                  <Input
                    id="spreadsheet_id"
                    value={sheetsConfig.spreadsheet_id}
                    onChange={(e) => setSheetsConfig({...sheetsConfig, spreadsheet_id: e.target.value})}
                    placeholder="Paste full URL or ID"
                  />
                </div>
                <div>
                  <Label htmlFor="sheet_name">Sheet Name</Label>
                  <Input
                    id="sheet_name"
                    value={sheetsConfig.sheet_name}
                    onChange={(e) => setSheetsConfig({...sheetsConfig, sheet_name: e.target.value})}
                    placeholder="JAMNAGAR INSTALATION"
                  />
                </div>
                <div>
                  <Label htmlFor="start_row">Start Row</Label>
                  <Input
                    id="start_row"
                    type="number"
                    value={sheetsConfig.start_row}
                    onChange={(e) => setSheetsConfig({...sheetsConfig, start_row: parseInt(e.target.value) || 2})}
                  />
                </div>
                <div className="bg-green-50 border border-green-200 p-3 rounded-md text-sm">
                  <p className="font-semibold mb-1 text-green-800">✅ Simple Import</p>
                  <p className="text-xs text-green-700">Paste URL → Click Import → Done!</p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowSheetsDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleGoogleSheetsImport} disabled={importing}>
                    {importing ? 'Importing...' : 'Try Import'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Search */}
      <Input
        placeholder="Search customers by name or phone..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="max-w-md"
      />

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{customers.length}</div>
              <p className="text-sm text-muted-foreground">Total Customers</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{customers.filter(c => c.has_co_applicant).length}</div>
              <p className="text-sm text-muted-foreground">With Co-Applicants</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{new Set(customers.map(c => c.branch)).size}</div>
              <p className="text-sm text-muted-foreground">Branches Active</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customers List */}
      <div className="grid gap-4">
        {filteredCustomers.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <User className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No customers found. Import from Google Sheets or add manually.</p>
            </CardContent>
          </Card>
        ) : (
          filteredCustomers.map((customer) => (
            <Card key={customer.customer_id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{customer.name}</h3>
                      <Badge variant="outline">{customer.branch}</Badge>
                      {customer.has_co_applicant && (
                        <Badge className="bg-purple-100 text-purple-800">Co-Applicant</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        {customer.phone}
                      </div>
                      {customer.email && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="h-4 w-4" />
                          {customer.email}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        {customer.address}
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline">
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
